import React, { useEffect, useState } from "react";
import { Bookmark, Copy, Check, Globe, Lock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import OracleShareComposer from "./OracleShareComposer";

/* Action bar that appears under every completed Oracle response.
 *
 * The share card is now the sole vehicle for distilled bullet points — a
 * previous "Where they overlap" flashcard was removed because it duplicated
 * the card's own truths list.
 *
 * Actions:
 *   • Save (private) — to your saves (no card generated at this stage; the
 *     user can produce one later from SavedItems)
 *   • Share to Weekly (public) — opens the full <OracleShareComposer/> modal:
 *     preview of the exchange with editable title, editable community-facing
 *     question, read-only Oracle response, and caption.
 *   • Copy
 */
export default function OracleResponseActions({ question, response, msgId, preShared }) {
  const { user } = useAuth();
  const [savedVis, setSavedVis] = useState(null); // null | "private" | "public"
  const [savedId, setSavedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  // Whether this response was already in the user's saves BEFORE this
  // session was opened — used to switch the copy from "Saved & shared"
  // (fresh action confirmation) to "Previously shared" (retroactive).
  const [wasPreExisting, setWasPreExisting] = useState(false);
  // Rehydrate from server-provided pre-shared info when the message
  // changes. Falls back to fresh actions if no match.
  useEffect(() => {
    if (preShared?.id) {
      setSavedId(preShared.id);
      setSavedVis(preShared.visibility || "private");
      setWasPreExisting(true);
    } else {
      setSavedId(null);
      setSavedVis(null);
      setWasPreExisting(false);
    }
    setComposerOpen(false);
  }, [msgId, preShared?.id, preShared?.visibility]);

  const requireAuth = () => {
    if (!user) { toast.error("Sign in to save Oracle responses."); return false; }
    return true;
  };

  // Private-save is the only path still using this helper — the public
  // Weekly path is fully owned by <OracleShareComposer/>.
  const savePrivate = async () => {
    if (!requireAuth()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/oracle/save", {
        question, response,
        visibility: "private",
        include_card: false,
      });
      setSavedId(data.saved.id);
      setSavedVis("private");
      toast.success("Saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const openShareComposer = () => {
    if (!requireAuth()) return;
    setComposerOpen(true);
  };

  const handlePublished = (saved) => {
    setSavedId(saved.id);
    setSavedVis("public");
  };

  const copy = async () => {
    const blob = `${question}\n\n${response}`;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(blob); ok = true; }
    } catch { /* fall through */ }
    if (!ok) {
      const ta = document.createElement("textarea");
      ta.value = blob; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      ta.remove();
    }
    setCopied(ok);
    if (ok) { toast.success("Copied."); setTimeout(() => setCopied(false), 1500); }
    else toast.error("Could not copy.");
  };

  return (
    <div data-testid="oracle-response-actions" className="mt-6">
      {/* PRIMARY ACTIONS */}
      <div className="flex flex-wrap items-center gap-3">
        {!savedId ? (
          <>
            <button
              data-testid="oracle-save-private"
              onClick={savePrivate}
              disabled={saving}
              className="mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[hsl(var(--oracle-accent)/0.5)] text-[hsl(var(--oracle-accent))] hover:border-[hsl(var(--oracle-accent))] hover:bg-[hsl(var(--oracle-accent)/0.08)] transition-colors inline-flex items-center gap-2 disabled:opacity-40"
              title="Save privately to your saves (only you can see it)"
            >
              <Bookmark size={12} />
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              data-testid="oracle-share-weekly"
              onClick={openShareComposer}
              disabled={saving}
              className="mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border-2 border-white text-white bg-transparent hover:bg-white/10 transition-colors inline-flex items-center gap-2 disabled:opacity-40"
              title="Save AND share publicly to the Weekly feed"
            >
              <Globe size={12} />
              {saving ? "Sharing…" : "Share to Weekly"}
            </button>
          </>
        ) : (
          <div data-testid="oracle-saved-indicator" className="mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[hsl(var(--oracle-accent))] text-[hsl(var(--oracle-accent))] inline-flex items-center gap-2">
            <Check size={12} />
            {savedVis === "public"
              ? <>{wasPreExisting ? "Previously shared" : "Saved & shared"} <Globe size={11} /></>
              : <>{wasPreExisting ? "Previously saved" : "Saved"} <Lock size={11} /></>}
            {savedVis !== "public" && (
              <button
                data-testid="oracle-make-public"
                onClick={openShareComposer}
                className="ml-3 text-white underline hover:text-white/80"
              >
                Share to Weekly
              </button>
            )}
          </div>
        )}

        <button
          data-testid="oracle-copy"
          onClick={copy}
          className="mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[hsl(var(--oracle-foreground)/0.2)] text-[hsl(var(--oracle-foreground)/0.7)] hover:border-[hsl(var(--oracle-foreground)/0.5)] transition-colors inline-flex items-center gap-2 ml-auto"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {/* FULL COMPOSER — preview + editable title/question/caption. Replaces
          the old inline caption-only flow. Oracle answer stays locked. */}
      <OracleShareComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        question={question}
        response={response}
        onPublished={handlePublished}
      />
    </div>
  );
}
